import { VStack } from '@chakra-ui/react'
import { FC, useEffect, useState } from 'react'
import { RoomMetadata } from '../../auto-generated-clients/content-delivery-client-v3.generated'
import { EditorViewTabs } from './EditorViewTabs'
import { EditorView } from './EditorView'
import { useLearningEvents } from '../../hooks/useLearningEvents'
import { LearningEvent } from '../../auto-generated-clients/learning-management-client-v1.generated'
import { useEventTemplates } from '../../hooks/useEventTemplates'
import { EventTemplateWithImplementation } from '../../models/eventTemplateWithImplementation'

interface EditorProps {
  selectedRoom: RoomMetadata | undefined
}

export const Editor: FC<EditorProps> = ({ selectedRoom }) => {
  const [selectedEvent, setSelectedEvent] = useState<LearningEvent>()

  const [selectedEventTemplate, setSelectedEventTemplate] =
    useState<EventTemplateWithImplementation>()

  const [isEditingEventTemplate, setIsEditingEventTemplate] = useState(false)

  const {
    isLoading,
    learningEvents,
    addLearningEventMutation,
    deleteLearningEventMutation,
    updateLearningEventMutation,
  } = useLearningEvents({
    contentMetadataId: selectedRoom?.contentMetadataId,
    releaseChannelId: selectedRoom?.releaseChannelId ?? '',
    selectedLearningEventId: selectedEvent?.learningEventId,
    setSelectedLearningEvent: setSelectedEvent,
    isEditingEventTemplate,
  })

  const {
    implementationsWithCompanyName,
    eventTemplatesToBeDisplayed,
    addEventTemplateMutation,
    deleteEventTemplateMutation,
    updateEventTemplateMutation,
    isEventTemplatesQueryLoading,
  } = useEventTemplates({
    selectedLearningEvent: selectedEvent,
    selectedEventTemplate,
    setSelectedEventTemplate,
    isEditingEventTemplate,
  })

  const onLearningEventClick = (event: LearningEvent) => {
    if (isEditingEventTemplate) {
      alert(
        'Please save or cancel the current event template before selecting another learning event.'
      )
      return
    }
    setSelectedEvent(event)
  }

  const onTemplateEventClick = (event: EventTemplateWithImplementation) => {
    if (isEditingEventTemplate) {
      alert(
        'Please save or cancel the current event template before selecting another template event.'
      )
      return
    }
    setSelectedEventTemplate(event)
  }

  useEffect(() => {
    if (learningEvents && learningEvents.length > 0) {
      setSelectedEvent(learningEvents[0])
    } else {
      setSelectedEvent(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoom])

  useEffect(() => {
    if (!selectedEvent && learningEvents && learningEvents.length > 0) {
      setSelectedEvent(learningEvents[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningEvents])

  useEffect(() => {
    if (eventTemplatesToBeDisplayed.length > 0) {
      setSelectedEventTemplate(eventTemplatesToBeDisplayed[0])
    } else {
      setSelectedEventTemplate(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent])

  if (!selectedRoom) {
    return null
  }

  return (
    <VStack
      height={'full'}
      px={8}
      py={4}
      width={'calc(100vw - 300px)'}
      maxWidth={'1600px'}
      gap={6}>
      <EditorViewTabs
        learningEvents={learningEvents ?? []}
        onAddLearningEvent={addLearningEventMutation}
        onEventClick={onLearningEventClick}
        selectedEvent={selectedEvent}
        selectedRoom={selectedRoom}
        isLoading={isLoading}
      />
      <EditorView
        selectedLearningEvent={selectedEvent}
        onDeleteLearningEvent={deleteLearningEventMutation}
        onEventClick={onTemplateEventClick}
        onTitleEditSubmit={updateLearningEventMutation}
        selectedEventTemplate={selectedEventTemplate}
        eventTemplates={eventTemplatesToBeDisplayed}
        addEventTemplate={addEventTemplateMutation}
        deleteEventTemplate={deleteEventTemplateMutation}
        updateEventTemplate={updateEventTemplateMutation}
        isLearningEventLoading={isLoading}
        isEventTemplatesLoading={isEventTemplatesQueryLoading}
        implementations={implementationsWithCompanyName ?? []}
        setIsEditingEventTemplate={setIsEditingEventTemplate}
      />
    </VStack>
  )
}
